import { Box, Slider, Typography } from '@mui/material';
import {
  DEFAULT_PRICE_DECIMAL_SEPARATOR,
  Languages,
} from '@/common/utils/constants';
import {
  priceSliderContainerStyles,
  priceSliderStyles,
  priceSliderWrapperStyles,
} from './PriceSlider.styled';
import { useEffect, useState } from 'react';

import { PrimaryColors } from '@/common/theme/constants';
import { QUERY_KEYS } from '@/common/types/react-query-keys.interfaces';
import { formatPrice } from '@/common/utils/formatPriceForDisplay';
import getSettings from '@/common/utils/getSettings';
import { useParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';

interface PriceSliderProps {
  minPrice: number;
  maxPrice: number;
  onChange: (value: number[]) => void;
  value: number[];
  translations: Record<string, string>;
}

export default function PriceSlider({
  minPrice,
  maxPrice,
  onChange,
  value,
  translations,
}: PriceSliderProps) {
  const [sliderValue, setSliderValue] = useState<number[]>(value);
  const { lang } = useParams();

  const { data: settings } = useQuery({
    queryKey: [QUERY_KEYS.SETTINGS, lang],
    queryFn: () => getSettings(lang as Languages),
  });

  useEffect(() => {
    setSliderValue(value);
  }, [value]);

  const handleChange = (_event: Event, newValue: number | number[]) => {
    const valueArray = Array.isArray(newValue) ? newValue : [0, newValue];
    setSliderValue(valueArray);
  };

  const handleChangeCommitted = (
    _event: React.SyntheticEvent | Event,
    newValue: number | number[]
  ) => {
    const valueArray = Array.isArray(newValue) ? newValue : [0, newValue];
    onChange(valueArray);
  };

  return (
    <Box sx={priceSliderContainerStyles}>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 'bold',
          textTransform: 'uppercase',
        }}
        fontWeight={500}
        mb={2}
      >
        {translations['filter.price'] || 'Price'}
      </Typography>
      <Box sx={priceSliderWrapperStyles}>
        <Slider
          value={sliderValue}
          onChange={handleChange}
          onChangeCommitted={handleChangeCommitted}
          valueLabelDisplay="auto"
          min={minPrice}
          max={maxPrice}
          sx={priceSliderStyles}
          valueLabelFormat={(value) =>
            `${settings?.currency} ${formatPrice(value, settings?.priceDecimalSeparator ?? DEFAULT_PRICE_DECIMAL_SEPARATOR)}`
          }
        />
      </Box>
      <Box display="flex" justifyContent="space-between" mt={1}>
        <Typography
          variant="body1"
          fontWeight={700}
          color={PrimaryColors.black}
        >
          {settings?.currency}
          {formatPrice(
            sliderValue[0],
            settings?.priceDecimalSeparator ?? DEFAULT_PRICE_DECIMAL_SEPARATOR
          )}
        </Typography>
        <Typography
          variant="body1"
          fontWeight={700}
          color={PrimaryColors.black}
        >
          {settings?.currency}
          {formatPrice(
            sliderValue[1],
            settings?.priceDecimalSeparator ?? DEFAULT_PRICE_DECIMAL_SEPARATOR
          )}
        </Typography>
      </Box>
    </Box>
  );
}
